import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Gift } from "lucide-react";
import PromoCodeInput from "@/components/PromoCodeInput";

const ROLE_OPTIONS = [
  "Designer",
  "Developer",
  "Marketer",
  "Copywriter",
  "Other",
];

const USE_CASE_OPTIONS = [
  { value: "scope_creep", label: "Scope creep" },
  { value: "late_pay", label: "Client late pay" },
  { value: "brief_changes", label: "Brief changes" },
  { value: "pricing_pushback", label: "Pricing pushback" },
  { value: "exploring", label: "Just exploring" },
];

export default function WelcomePage({ user, refreshUser }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [useCase, setUseCase] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPromo, setShowPromo] = useState(false);

  useEffect(() => {
    if (!user) return;
    const fallback =
      user.name ||
      (user.email ? user.email.split("@")[0] : "") ||
      "";
    setName(fallback);
  }, [user]);

  const handleContinue = async () => {
    if (!name.trim() || !role || !useCase) {
      toast.error("Pick a role and primary use case so we can show you the right thing first.");
      return;
    }
    setSubmitting(true);
    try {
      await api.put("/users/me", {
        name: name.trim(),
        role,
        primary_use_case: useCase,
      });
      await refreshUser?.();
      navigate("/scope-guard?tutorial=1", { replace: true });
    } catch (e) {
      console.error("Failed to save profile", e);
      toast.error("Couldn't save that — try again?");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    setSubmitting(true);
    try {
      await api.put("/users/me", { tutorial_completed: true });
      await refreshUser?.();
      navigate("/dashboard", { replace: true });
    } catch (e) {
      console.error("Skip failed", e);
      navigate("/dashboard", { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-[480px] bg-slate-800/60 border border-slate-700 border-l-4 border-l-amber-600 rounded-2xl p-8 shadow-2xl">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-slate-50" data-testid="welcome-heading">
            Hi {user?.name ? user.name.split(" ")[0] : "there"}.
          </h1>
          <p className="text-slate-400 text-sm mt-2">
            Tell us about yourself so we can show you the right thing first.
          </p>
        </div>

        <div className="space-y-5">
          <div>
            <Label htmlFor="welcome-name" className="text-slate-300">
              Your name
            </Label>
            <Input
              id="welcome-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Designer"
              className="mt-1 bg-slate-900 border-slate-700 text-slate-100"
              data-testid="welcome-name-input"
            />
          </div>

          <div>
            <Label className="text-slate-300">What's your work?</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger
                className="mt-1 bg-slate-900 border-slate-700 text-slate-100"
                data-testid="welcome-role-select"
              >
                <SelectValue placeholder="Pick one" />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-slate-300">
              What's the trickiest client situation you face?
            </Label>
            <Select value={useCase} onValueChange={setUseCase}>
              <SelectTrigger
                className="mt-1 bg-slate-900 border-slate-700 text-slate-100"
                data-testid="welcome-use-case-select"
              >
                <SelectValue placeholder="Pick one" />
              </SelectTrigger>
              <SelectContent>
                {USE_CASE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Promo-code entry — subordinate to the profile form. Cold-DM users
            arrive with a channel-attributed code (06-04) and shouldn't have to
            hunt for /billing after signing in. Reuses PromoCodeInput as-is. */}
        <div className="mt-5 pt-4 border-t border-slate-700/60">
          {showPromo ? (
            <PromoCodeInput onSuccess={() => refreshUser?.()} />
          ) : (
            <button
              type="button"
              onClick={() => setShowPromo(true)}
              className="text-slate-500 hover:text-slate-300 text-sm transition flex items-center gap-1.5"
              data-testid="welcome-promo-toggle"
            >
              <Gift className="w-3.5 h-3.5" />
              Have a promo code?
            </button>
          )}
        </div>

        <div className="mt-8 flex flex-col gap-3">
          <Button
            onClick={handleContinue}
            disabled={submitting}
            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-medium"
            data-testid="welcome-continue-btn"
          >
            {submitting ? "Saving..." : "Continue"}
          </Button>
          <button
            type="button"
            onClick={handleSkip}
            disabled={submitting}
            className="text-slate-500 hover:text-slate-300 text-sm transition"
            data-testid="welcome-skip-btn"
          >
            Skip — you'll miss the demo
          </button>
        </div>
      </div>
    </div>
  );
}
